// ============================================================================
// The notes pause releases by itself (SPEC 2026-10-21 B4).
// ----------------------------------------------------------------------------
// Typing in Stage 1's site notes pauses the AI (App's pauseAiForInspectorNotes).
// The pause used to release only on Continue or on leaving for the Stage 1.5
// page; with that page gone, Full AI would wait on a press. It now releases as
// if Continue were pressed once the operator leaves the notes (Stage1NotesEditor's
// onBlur, when focus moves elsewhere in the app), or 10 s after the last
// keystroke with the caret already out of the notes.
//
// While the caret is still in the notes, a pause in typing is not the end of the
// edit: releasing then let the 2 s watcher buy a summary that the next keystroke
// aborted, at every pause of 12 s or more (review 11). With the caret there the
// pause holds for a minute of no keystrokes, so an operator who stops typing and
// waits for the AI is never left waiting on a press.
// ============================================================================
import { useEffect, useRef } from 'react';

export const INSPECTOR_NOTES_PAUSE_RELEASE_MS = 10000;
/** With the caret still in the notes, how long without a keystroke counts as done. */
export const INSPECTOR_NOTES_IDLE_RELEASE_MS = 60000;

export function useInspectorNotesPauseRelease({
    paused,
    notes,
    onRelease,
    isEditing = () => false,
    delayMs = INSPECTOR_NOTES_PAUSE_RELEASE_MS,
    idleMs = INSPECTOR_NOTES_IDLE_RELEASE_MS,
}: {
    paused: boolean;
    /** The notes text: every keystroke changes it, which restarts the wait. */
    notes: unknown;
    onRelease: () => void;
    /** The caret is in the notes editor (read when the wait ends). */
    isEditing?: () => boolean;
    delayMs?: number;
    idleMs?: number;
}): void {
    // Read when the timer fires, so the release always runs the current closure.
    const onReleaseRef = useRef(onRelease);
    onReleaseRef.current = onRelease;
    const isEditingRef = useRef(isEditing);
    isEditingRef.current = isEditing;
    useEffect(() => {
        if (!paused) return undefined;
        let timer = setTimeout(() => {
            if (!isEditingRef.current()) {
                onReleaseRef.current();
                return;
            }
            timer = setTimeout(() => onReleaseRef.current(), Math.max(0, idleMs - delayMs));
        }, delayMs);
        return () => clearTimeout(timer);
    }, [paused, notes, delayMs, idleMs]);
}
